import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { CheckCircle2, Circle, Package, Truck, MapPin, Home, ShoppingBag, Sparkles, ChevronRight, ExternalLink, FileDown } from "lucide-react";
import { courierName, trackingUrl } from "../../lib/couriers";

const STAGES = ["placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"];
const LABEL = {placed:"Placed", confirmed:"Confirmed", packed:"Packed", shipped:"Shipped", out_for_delivery:"Out for delivery", delivered:"Delivered"};
const STAGE_ICON = {placed: ShoppingBag, confirmed: CheckCircle2, packed: Package, shipped: Truck, out_for_delivery: Truck, delivered: Home};

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${orderId}`).then(r=>setO(r.data)).catch(()=>{}); }, [orderId]);
  if (!o) return <div className="py-16 text-center text-stone-500">Loading…</div>;
  const currIdx = Math.max(STAGES.indexOf(o.status), 0);
  const isCancelled = ["cancelled","returned","refunded"].includes(o.status);

  return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: -50, right: -180 }}/>
      <div className="blob blob-sage" style={{ width: 400, height: 400, top: 500, left: -140 }}/>
      <div className="blob blob-terra" style={{ width: 350, height: 350, bottom: 100, right: -100 }}/>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 relative z-10">
        {/* Confetti-style hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#D4AF37]/40 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]"/>
            <span className="uppercase text-[10px] tracking-[0.25em] text-[#8B7355] font-semibold">Order confirmed</span>
          </div>
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-[#2E4A35]/10 animate-ping"/>
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#2E4A35] to-[#1a3020] flex items-center justify-center shadow-2xl shadow-[#2E4A35]/30">
              <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5}/>
            </div>
          </div>
          <h1 className="font-serif-display text-6xl md:text-7xl leading-[0.95]">Thank you!</h1>
          <p className="text-stone-600 mt-4 text-lg max-w-lg mx-auto">
            Order <span className="font-outfit font-bold text-[#3B2F2F]" data-testid="order-no">{o.order_no}</span> is on its way. Confirmation is in your inbox.
          </p>
        </div>

        {/* Timeline card */}
        <div className="mt-12 bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-8 md:p-10 shadow-xl shadow-[#3B2F2F]/5">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-1">Order journey</div>
              <h2 className="font-serif-display text-3xl">Track your parcel</h2>
            </div>
            {!isCancelled && (
              <div className="text-xs text-stone-500 hidden md:block">
                Stage <span className="font-outfit font-bold text-[#3B2F2F]">{currIdx + 1}</span> of {STAGES.length}
              </div>
            )}
          </div>

          {isCancelled ? (
            <div className="bg-[#F5DEC7] border border-[#993322]/30 rounded-2xl p-5 text-sm text-[#3B2F2F]">
              This order was marked as <span className="font-outfit font-semibold uppercase">{o.status}</span>. Please contact support if you have questions.
            </div>
          ) : (
            <div className="relative">
              {/* Track line */}
              <div className="absolute top-6 left-6 right-6 h-1 bg-stone-200 rounded-full"/>
              <div className="absolute top-6 left-6 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8C87C] rounded-full transition-all duration-500"
                   style={{width: `calc((100% - 48px) * ${currIdx/(STAGES.length-1)})`}}/>
              <div className="grid grid-cols-6 gap-1 relative">
                {STAGES.map((s, i) => {
                  const Icon = STAGE_ICON[s] || Circle;
                  const done = i <= currIdx;
                  const isCurrent = i === currIdx;
                  return (
                    <div key={s} className="flex flex-col items-center text-center" data-testid={`stage-${s}`}>
                      <div className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        done ? "bg-gradient-to-br from-[#D4AF37] to-[#B8942C] shadow-lg shadow-[#D4AF37]/40 scale-100"
                             : "bg-white border-2 border-stone-200 scale-90 opacity-60"}`}>
                        {isCurrent && <div className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-40"/>}
                        <Icon className={`w-5 h-5 relative ${done ? "text-[#3B2F2F]" : "text-stone-400"}`}/>
                      </div>
                      <div className={`text-[10px] md:text-xs mt-2 font-outfit ${done?"font-semibold text-stone-800":"text-stone-400"}`}>
                        {LABEL[s]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent activity */}
          {o.timeline && o.timeline.length > 0 && (
            <div className="mt-8 pt-6 border-t border-dashed border-stone-200">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-3">Activity</div>
              <div className="space-y-2">
                {o.timeline.slice().reverse().slice(0, 3).map((t, i) => (
                  <div key={i} className="text-sm flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-1.5 shrink-0"/>
                    <div className="flex-1">
                      <span className="font-outfit font-medium text-stone-800 capitalize">{t.status?.replace(/_/g," ")}</span>
                      <span className="text-stone-500"> · {t.note}</span>
                    </div>
                    <span className="text-xs text-stone-400 shrink-0">{new Date(t.at).toLocaleString(undefined,{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Items + Delivery */}
        <div className="mt-6 grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FEF3E2] flex items-center justify-center"><Package className="w-5 h-5 text-[#D4AF37]"/></div>
              <h3 className="font-serif-display text-2xl">What's in your parcel</h3>
            </div>
            <div className="space-y-3">
              {o.items.map(i => (
                <div key={i.variant_sku} className="flex gap-4 items-center p-3 rounded-2xl hover:bg-[#F5F0E6]/50 transition-colors">
                  <div className="relative">
                    <img src={i.image} className="w-16 h-16 rounded-xl object-cover border border-stone-100" alt=""/>
                    <span className="absolute -top-1.5 -right-1.5 bg-[#3B2F2F] text-white text-[10px] font-outfit font-bold w-5 h-5 rounded-full flex items-center justify-center">{i.qty}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-800">{i.product_name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{i.variant_label} · {inr(i.unit_price)} each</div>
                  </div>
                  <div className="font-outfit font-bold text-[#3B2F2F]">{inr(i.line_total)}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-dashed border-stone-200 space-y-1.5 text-sm">
              <Row l="Subtotal" v={inr(o.subtotal)}/>
              {o.discount > 0 && <Row l="Discount" v={`- ${inr(o.discount)}`} accent/>}
              <Row l="Shipping" v={o.shipping===0 ? <span className="text-[#2E4A35] font-semibold">FREE</span> : inr(o.shipping)}/>
              <Row l="Tax" v={inr(o.tax)}/>
              <div className="flex justify-between font-outfit font-bold text-lg pt-3 mt-2 border-t border-stone-200">
                <span>Total paid</span><span className="text-[#3B2F2F]">{inr(o.total)}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 space-y-6">
            {/* Shipment tracking */}
            <ShipmentTracking awb={o.awb_number} courier={o.courier} status={o.status}/>

            <div className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#E8EDDF] flex items-center justify-center"><MapPin className="w-5 h-5 text-[#2E4A35]"/></div>
                <h3 className="font-serif-display text-2xl">Delivery</h3>
              </div>
              <div className="text-sm text-stone-700 space-y-1 leading-relaxed">
                <div className="font-medium text-stone-900">{o.address?.line1}</div>
                <div>{o.address?.city}, {o.address?.state} {o.address?.pincode}</div>
                <div>{o.address?.country}</div>
                <div className="pt-3 mt-3 border-t border-dashed border-stone-200 text-xs text-stone-500 space-y-0.5">
                  <div>{o.email}</div><div>{o.phone}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#3B2F2F] to-[#2A2020] text-[#FDFBF7] rounded-3xl p-6 relative overflow-hidden">
              <div className="pattern-beans absolute inset-0 opacity-20"/>
              <div className="relative">
                <div className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37] mb-2">What's next?</div>
                <div className="font-serif-display text-2xl leading-tight mb-3">Your coffee is being lovingly prepared.</div>
                <p className="text-xs text-[#FDFBF7]/70 leading-relaxed">We'll email you tracking as soon as your parcel is dispatched. Usually within 24 hours.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/invoice.pdf`}
             target="_blank" rel="noopener noreferrer"
             data-testid="download-invoice-btn"
             className="inline-flex items-center gap-2 bg-white border-2 border-[#3B2F2F] text-[#3B2F2F] rounded-full px-6 py-3 hover:bg-[#3B2F2F] hover:text-white transition-colors">
            <FileDown className="w-4 h-4"/> Download PDF invoice
          </a>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-6 py-3 hover:bg-black">
            Continue shopping <ChevronRight className="w-4 h-4"/>
          </Link>
          <Link to="/account" className="inline-flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#3B2F2F] px-2">
            View all orders <ChevronRight className="w-3 h-3"/>
          </Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v, accent }) => (
  <div className="flex justify-between"><span className="text-stone-500">{l}</span><span className={accent?"text-[#2E4A35] font-semibold":""}>{v}</span></div>
);

function ShipmentTracking({ awb, courier, status }) {
  if (!awb) {
    return (
      <div className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 shadow-sm" data-testid="shipment-tracking-empty">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#FEF3E2] flex items-center justify-center"><Truck className="w-5 h-5 text-[#D4AF37]"/></div>
          <h3 className="font-serif-display text-2xl">Shipment tracking</h3>
        </div>
        <p className="text-sm text-stone-600 leading-relaxed">
          Shipment tracking will be available once your order is dispatched. We'll email you the tracking link as soon as it's on its way.
        </p>
      </div>
    );
  }
  const url = trackingUrl(courier, awb);
  const name = courierName(courier);
  return (
    <div className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 shadow-sm" data-testid="shipment-tracking">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-[#FEF3E2] flex items-center justify-center"><Truck className="w-5 h-5 text-[#D4AF37]"/></div>
        <h3 className="font-serif-display text-2xl">Shipment tracking</h3>
      </div>
      <div className="space-y-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-outfit font-semibold">Courier</div>
          <div className="text-sm text-[#3B2F2F] font-outfit font-semibold" data-testid="shipment-courier">{name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-outfit font-semibold">AWB number</div>
          <div className="font-mono text-base text-[#3B2F2F] break-all" data-testid="shipment-awb">{awb}</div>
        </div>
        {status && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-outfit font-semibold">Courier status</div>
            <div className="text-sm text-stone-800 capitalize">{String(status).replace(/_/g," ")}</div>
          </div>
        )}
        <a href={url} target="_blank" rel="noopener noreferrer"
           data-testid="shipment-track-btn"
           className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-black w-full justify-center mt-2">
          Track with {name} <ExternalLink className="w-3.5 h-3.5"/>
        </a>
      </div>
    </div>
  );
}
